import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';

import { formatDate } from '@zen/utils/dateTime';

import type { AuditLogField, AuditLogNode } from '../AuditLogs/types';
import { AuditLogsDocument, type AuditLogsQueryResult, type AuditLogsQueryVariables } from '../graphql';

export interface AuditLogsFilters {
  actorId?: string[];
  entityId?: string[];
  entityType?: string[];
  field?: AuditLogField[];
  parentEntityId?: string[];
  parentEntityType?: string[];
  source?: string[];
}

interface UseAuditLogsGroupedOptions {
  isVisible: boolean;
  preFilters?: AuditLogsFilters;
}

export const useAuditLogsGrouped = ({ isVisible, preFilters }: UseAuditLogsGroupedOptions) => {
  const { data, loading, error } = useQuery<AuditLogsQueryResult, AuditLogsQueryVariables>(AuditLogsDocument, {
    variables: {
      auditLogsFiltersInput: {
        ...preFilters
      }
    },
    skip: !isVisible,
    fetchPolicy: 'network-only'
  });

  const groupedLogs = useMemo(() => {
    const nodes = data?.auditLogs?.nodes ?? [];

    return nodes.reduce<Record<string, AuditLogNode[]>>((acc, log) => {
      const dateKey = log.actionTimestamp ? formatDate(log.actionTimestamp, 'D_MMMM_YYYY') : 'Unknown Date';

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(log);
      return acc;
    }, {});
  }, [data]);

  return {
    groupedLogs,
    isLoading: loading && !data,
    error
  };
};
